/* Detailing1 — Schnellbestellung, Fassung 2 (Paket D3, 2026-09-03)
 * Ein Custom Element für drei Einsatzorte (Collection-Ansicht, Produktseite, Cart-Seite/Sammelliste).
 * Menge in der Zeile = Menge im Warenkorb: Plus/Minus und Eingaben schreiben direkt in den Warenkorb
 * (/cart/add.js bzw. /cart/change.js), Bestandsgrenzen kommen vom Server zurück. Kein Sammelschritt,
 * keine Gesamtsumme mehr. Suche (Sammelliste) wie bisher über die Section-Rendering-API.
 */
if (!customElements.get('d1-quick-order')) {
  class D1QuickOrder extends HTMLElement {
    connectedCallback() {
      if (this.listeners) this.listeners.abort();
      this.listeners = new AbortController();
      const listenerOptions = { signal: this.listeners.signal };
      this.ctx = this.dataset.context || 'pdp';
      this.rows = this.querySelector('[data-qo-rows]');
      this.msgEl = this.querySelector('[data-qo-msg]');
      this.emptyEl = this.querySelector('[data-qo-empty]');
      this.searchForm = this.querySelector('[data-qo-search-form]');
      this.searchInput = this.querySelector('[data-qo-search]');
      this.searchStatus = this.querySelector('[data-qo-search-status]');
      this.maxRows = parseInt(this.dataset.maxRows, 10) || 60;
      this.cartQty = this.cartQty || {};
      this.timers = this.timers || new Map();
      this.pending = this.pending || new Set();
      this.desired = this.desired || new Map();
      if (!this.rows) return;

      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-qo-qty]')) this.schedule(e.target.closest('tr[data-vid]'), 400);
      }, listenerOptions);
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && e.target.matches('[data-qo-qty]')) { e.preventDefault(); this.schedule(e.target.closest('tr[data-vid]'), 0); }
      }, listenerOptions);
      this.addEventListener('click', (e) => this.onClick(e), listenerOptions);

      if (this.searchForm) this.searchForm.addEventListener('submit', (e) => { e.preventDefault(); this.search(true); }, listenerOptions);
      if (this.searchInput) this.searchInput.addEventListener('input', () => this.scheduleSearch(), listenerOptions);

      const more = this.querySelector('[data-qo-more]');
      if (more) this.collapse(parseInt(more.dataset.collapse, 10) || 30);

      this.loadCart().then(() => this.applyCart());
      // Änderungen im Cart-Drawer / auf der Warenkorbseite übernehmen
      this.cartListener = (evt) => {
        if (evt.detail && evt.detail.cart && Array.isArray(evt.detail.cart.items)) {
          this.setCart(evt.detail.cart);
          this.applyCart();
        } else {
          this.loadCart().then(() => this.applyCart());
        }
      };
      ['on:cart:add', 'on:cart:change', 'on:line-item:change'].forEach((name) => {
        document.addEventListener(name, this.cartListener, listenerOptions);
      });
    }

    disconnectedCallback() {
      if (this.listeners) this.listeners.abort();
      if (this.timers) { this.timers.forEach(clearTimeout); this.timers.clear(); }
      if (this.desired) this.desired.clear();
      clearTimeout(this.searchTimer);
      if (this.searchAbort) this.searchAbort.abort();
      this.cartReadId = (this.cartReadId || 0) + 1;
    }

    /* ---------- Warenkorb-Stand ---------- */

    async loadCart() {
      const readId = this.cartReadId = (this.cartReadId || 0) + 1;
      const revision = this.cartRevision || 0;
      try {
        const cartUrl = (window.theme && theme.routes && theme.routes.cart) || this.dataset.cartUrl || '/cart';
        const response = await fetch(`${cartUrl}.js`, { cache: 'no-store', headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const cart = await response.json();
        if (this.isConnected && readId === this.cartReadId && revision === (this.cartRevision || 0) && Array.isArray(cart.items)) this.setCart(cart);
      } catch (e) { /* offline: Stand bleibt */ }
    }

    setCart(cart) {
      this.cartRevision = (this.cartRevision || 0) + 1;
      const map = {};
      (cart && cart.items ? cart.items : []).forEach((i) => { map[i.variant_id] = (map[i.variant_id] || 0) + i.quantity; });
      this.cartQty = map;
    }

    /* Zeilen an den Warenkorb angleichen (Menge, Aktiv-Zustand, Staffel-Chip). */
    applyCart(rows) {
      if (!this.isConnected) return;
      (rows || this.rowEls()).forEach((row) => this.refreshRow(row, true));
      if (this.emptyEl) this.emptyEl.hidden = this.rowEls().length > 0;
    }

    recalc() { this.applyCart(); } // Kompatibilität (ältere Aufrufer)

    refreshRow(row, setInput) {
      const qty = this.cartQty[row.dataset.vid] || 0;
      const input = row.querySelector('[data-qo-qty]');
      if (input && setInput && !this.pending.has(row.dataset.vid) && !this.desired.has(row.dataset.vid)) input.value = qty;
      row.classList.toggle('d1-qo__row--active', qty > 0);
      const t = this.tiers(row);
      if (t) {
        const tier = this.tierFor(t, qty);
        row.querySelectorAll('.d1-qo__tier').forEach((chip) => {
          chip.classList.toggle('is-active', qty > 0 && Number(chip.dataset.q) === tier.q);
        });
      }
      if (!this.pending.has(row.dataset.vid)) this.state(row, '');
    }

    /* ---------- Zeilen & Mengenregeln ---------- */

    rowEls() { return Array.from(this.rows.querySelectorAll('tr[data-vid]')); }

    rule(row) {
      return {
        min: parseInt(row.dataset.min, 10) || 1,
        inc: parseInt(row.dataset.inc, 10) || 1,
        max: row.dataset.max ? parseInt(row.dataset.max, 10) : null
      };
    }

    /* 0 = nicht im Warenkorb. Sonst auf min / Schrittweite / max runden (B2B-Mengenregeln). */
    normalize(row, raw) {
      const r = this.rule(row);
      let q = Math.floor(Number(raw) || 0);
      if (q <= 0) return 0;
      if (q < r.min) q = r.min;
      if (r.inc > 1) {
        const over = (q - r.min) % r.inc;
        if (over) q += r.inc - over;
      }
      if (r.max && q > r.max) q = r.max - ((r.max - r.min) % r.inc);
      return q;
    }

    qtyOf(row) {
      const input = row.querySelector('[data-qo-qty]');
      return input ? (parseInt(input.value, 10) || 0) : 0;
    }

    tiers(row) {
      try { return JSON.parse(row.dataset.tiers); } catch (e) { return null; }
    }

    tierFor(t, qty) {
      let tier = { c: Number(t.c), q: 0 };
      (t.b || []).forEach((b) => {
        const q = Number(b.q);
        if (qty >= q && q >= tier.q) tier = { c: Number(b.c), q };
      });
      return tier;
    }

    state(row, text, kind) {
      const el = row.querySelector('[data-qo-state]');
      if (!el) return;
      el.textContent = text || '';
      if (kind) el.dataset.state = kind; else delete el.dataset.state;
    }

    collapse(n) { this.rowEls().forEach((row, i) => { row.hidden = i >= n; }); }

    /* ---------- Direkt in den Warenkorb ---------- */

    schedule(row, delay) {
      if (!this.isConnected || !row || !row.isConnected) return;
      this.desired.set(row.dataset.vid, this.normalize(row, this.qtyOf(row)));
      clearTimeout(this.timers.get(row));
      this.timers.set(row, setTimeout(() => { this.timers.delete(row); this.sync(row); }, delay));
    }

    async sync(row) {
      if (!this.isConnected || !row || !row.isConnected) return;
      const input = row.querySelector('[data-qo-qty]');
      if (!input) return;
      const vid = row.dataset.vid;
      const want = this.normalize(row, input.value);
      input.value = want;
      this.desired.set(vid, want);
      const have = this.cartQty[vid] || 0;
      // Keep the latest requested quantity until the current response has been reconciled.
      if (this.pending.has(vid)) return;
      // change.js returns a whole-cart snapshot; serialize rows so an older snapshot cannot
      // overwrite another row's completed update.
      if (this.pending.size) return;
      if (want === have) { this.desired.delete(vid); this.refreshRow(row); return; }

      this.pending.add(vid);
      this.cartRevision = (this.cartRevision || 0) + 1;
      let succeeded = false;
      row.classList.add('is-syncing');
      this.state(row, 'Wird aktualisiert …');
      const headers = { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
      try {
        let res;
        if (have === 0) {
          res = await fetch(this.dataset.cartAddUrl || '/cart/add.js', {
            method: 'POST', headers,
            body: JSON.stringify({ items: [{ id: Number(vid), quantity: want }], sections: 'cart-icon-bubble', sections_url: window.location.pathname })
          });
        } else {
          const changeUrl = (window.theme && theme.routes && theme.routes.cartChange) || `${this.dataset.cartUrl || '/cart'}/change.js`;
          res = await fetch(changeUrl, {
            method: 'POST', headers,
            body: JSON.stringify({ id: String(vid), quantity: want, sections: 'cart-icon-bubble', sections_url: window.location.pathname })
          });
        }
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          let msg = data.description || data.message || 'Konnte nicht in den Warenkorb gelegt werden.';
          if (data.errors && typeof data.errors === 'object') msg = Object.values(data.errors).flat().join(', ');
          this.pending.delete(vid);
          this.desired.delete(vid);
          clearTimeout(this.timers.get(row));
          this.timers.delete(row);
          await this.loadCart();
          this.refreshRow(row, true);
          this.state(row, msg, 'error');
          return;
        }
        this.cartRevision = (this.cartRevision || 0) + 1;
        this.updateBubble(data.sections);
        // Stand direkt aus der Antwort: add.js liefert die Zeile(n), change.js den ganzen Warenkorb
        if (have === 0 && Array.isArray(data.items)) {
          data.items.forEach((i) => { this.cartQty[i.variant_id] = i.quantity; });
        } else if (Array.isArray(data.items)) {
          this.setCart(data);
        } else {
          await this.loadCart();
        }
        this.pending.delete(vid);
        if (this.desired.get(vid) === want && this.normalize(row, input.value) !== want) {
          this.desired.set(vid, this.normalize(row, input.value));
        }
        const latest = this.desired.get(vid);
        if (latest === want) this.desired.delete(vid);
        else if (latest !== undefined) input.value = latest;
        this.refreshRow(row, true);
        const now = this.cartQty[vid] || 0;
        if (want > 0 && now < want) this.state(row, `Nur ${now} verfügbar`, 'error');
        else this.state(row, '');
        succeeded = true;
        this.notifyCart();
      } catch (err) {
        this.pending.delete(vid);
        this.desired.delete(vid);
        clearTimeout(this.timers.get(row));
        this.timers.delete(row);
        input.value = have;
        this.refreshRow(row);
        this.state(row, 'Netzwerkfehler. Bitte erneut versuchen.', 'error');
      } finally {
        this.pending.delete(vid);
        row.classList.remove('is-syncing');
        if (this.isConnected && !this.pending.size) {
          const next = succeeded && this.desired.has(vid) && row.isConnected
            ? row : this.rowEls().find((item) => this.desired.has(item.dataset.vid));
          if (next) this.schedule(next, 0);
        }
      }
    }

    updateBubble(sections) {
      if (!sections || !sections['cart-icon-bubble']) return;
      const el = document.getElementById('cart-icon-bubble');
      if (!el) return;
      const doc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      const fresh = doc.querySelector('.shopify-section') || doc.body;
      el.innerHTML = fresh.innerHTML;
    }

    notifyCart() {
      const cartUrl = (window.theme && theme.routes && theme.routes.cart) || this.dataset.cartUrl || '/cart';
      const onCartPage = window.location.pathname === cartUrl;
      const cartItems = document.querySelector('cart-items');
      if (onCartPage && cartItems) {
        // Warenkorbseite: Tabelle neu rendern; war der Warenkorb leer, fehlt die Zusammenfassung -> neu laden
        if (cartItems.dataset.empty === 'true' || typeof cartItems.refresh !== 'function') {
          window.location.reload();
          return;
        }
        cartItems.refresh();
        return;
      }
      // sonst (Collection, Produktseite): nur Drawer/Bubble aktualisieren, kein Öffnen
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
    }

    /* ---------- Klicks ---------- */

    onClick(e) {
      const t = e.target;
      if (!t || !t.closest) return;
      const remove = t.closest('[data-qo-remove]');
      if (remove) {
        const row = remove.closest('tr[data-vid]');
        if (row) {
          clearTimeout(this.timers.get(row));
          this.timers.delete(row);
          this.desired.delete(row.dataset.vid);
          row.remove();
        }
        if (this.emptyEl) this.emptyEl.hidden = this.rowEls().length > 0;
        return;
      }
      if (t.closest('[data-qo-clear]')) {
        if (this.ctx === 'list') this.rowEls().forEach((row) => row.remove());
        if (this.emptyEl) this.emptyEl.hidden = this.rowEls().length > 0;
        this.say('');
        return;
      }
      const more = t.closest('[data-qo-more]');
      if (more) {
        this.rowEls().forEach((row) => { row.hidden = false; });
        more.hidden = true;
      }
    }

    say(text, state) {
      if (!this.msgEl) return;
      this.msgEl.textContent = text;
      this.msgEl.hidden = !text;
      if (state) this.msgEl.setAttribute('data-state', state); else this.msgEl.removeAttribute('data-state');
    }

    /* ---------- Suche (nur Sammelliste) ---------- */

    setSearchStatus(text, state) {
      if (!this.searchStatus) return;
      this.searchStatus.textContent = text;
      if (state) this.searchStatus.setAttribute('data-state', state); else this.searchStatus.removeAttribute('data-state');
    }

    scheduleSearch() {
      clearTimeout(this.searchTimer);
      if (this.searchAbort) this.searchAbort.abort();
      const q = this.searchInput.value.trim();
      if (q.length < 3) { this.setSearchStatus(''); return; }
      this.searchTimer = setTimeout(() => this.search(false), 350);
    }

    async search(submitted) {
      if (!this.searchInput) return;
      clearTimeout(this.searchTimer);
      const q = this.searchInput.value.trim();
      if (!q) return;
      if (this.searchAbort) this.searchAbort.abort();
      this.searchAbort = new AbortController();
      const searchAbort = this.searchAbort;
      this.setSearchStatus('Suche …');

      const url = `${this.dataset.searchUrl}?q=${encodeURIComponent(q)}&type=product&options%5Bprefix%5D=last&section_id=${encodeURIComponent(this.dataset.resultsSection)}`;
      try {
        const res = await fetch(url, { signal: searchAbort.signal, headers: { Accept: 'text/html' } });
        if (!res.ok) throw new Error(String(res.status));
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        if (searchAbort.signal.aborted || !this.isConnected || this.searchInput.value.trim() !== q) return;
        const wrap = doc.querySelector('.d1-qo-results');
        const found = wrap ? Array.from(wrap.querySelectorAll('tr[data-vid]')) : [];
        const notes = wrap ? Array.from(wrap.querySelectorAll('tr[data-note]')) : [];
        if (!found.length) { this.setSearchStatus(`Keine Treffer für „${q}“.`, 'error'); return; }

        const exact = found.filter((row) => row.dataset.exact === 'true');
        const take = exact.length ? exact : found;
        const added = this.appendRows(take);

        // Scanner-/Exaktmodus: genau eine Variante -> eine Einheit mehr direkt in den Warenkorb, weiter scannen.
        if (take.length === 1 && (exact.length === 1 || submitted)) {
          const row = this.rows.querySelector(`tr[data-vid="${take[0].dataset.vid}"]`);
          this.bump(row);
          this.schedule(row, 0);
          this.searchInput.value = '';
          this.searchInput.focus();
          const name = row.querySelector('.d1-qo__name');
          this.setSearchStatus(`${name ? name.textContent.trim() : 'Artikel'} in den Warenkorb gelegt.`, 'ok');
        } else {
          let text = `${take.length} Treffer für „${q}“ in die Liste übernommen`;
          if (added < take.length) text += ` (${take.length - added} bereits vorhanden)`;
          if (notes.length) text += ` – ${notes.map((n) => n.textContent.trim()).join(' ')}`;
          this.setSearchStatus(`${text}.`);
          const first = this.rows.querySelector(`tr[data-vid="${take[0].dataset.vid}"] [data-qo-qty]`);
          if (first && !submitted) first.scrollIntoView({ block: 'nearest' });
          if (first && submitted) first.focus();
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        this.setSearchStatus('Suche fehlgeschlagen. Bitte erneut versuchen.', 'error');
      }
    }

    appendRows(list) {
      let added = 0;
      const fresh = [];
      list.forEach((tr) => {
        if (this.rows.querySelector(`tr[data-vid="${tr.dataset.vid}"]`)) return;
        const node = document.importNode(tr, true);
        this.rows.appendChild(node);
        fresh.push(node);
        added += 1;
      });
      this.trim();
      this.applyCart(fresh);
      if (this.emptyEl) this.emptyEl.hidden = this.rowEls().length > 0;
      return added;
    }

    trim() {
      const rows = this.rowEls();
      let excess = rows.length - this.maxRows;
      if (excess <= 0) return;
      rows.filter((row) => (this.cartQty[row.dataset.vid] || 0) === 0).forEach((row) => {
        if (excess > 0) { row.remove(); excess -= 1; }
      });
    }

    bump(row) {
      const input = row.querySelector('[data-qo-qty]');
      if (!input) return;
      const r = this.rule(row);
      const cur = this.qtyOf(row);
      input.value = cur > 0 ? this.normalize(row, cur + r.inc) : r.min;
    }
  }

  customElements.define('d1-quick-order', D1QuickOrder);
}
